import _ from 'lodash'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { AppState } from 'common/store/reducer'
import { getMoodMeterGroupsService, getMyGroupsService } from 'page-modules/mood-meter/service'
import { MoodMeterGroup } from 'page-modules/mood-meter/service/types'

import {
  CHANGE_GROUP_PLOTS_NAME_FILTER,
  CHANGE_GROUP_PLOTS_ORDER_FILTER,
  CHANGE_GROUP_PLOTS_PAGE,
  LOAD_GROUP_PLOTS_PAGE,
  RELOAD_GROUP_PLOTS_AFTER_DELETE,
  changeGroupPlotsNameFilter,
  changeGroupPlotsPage,
} from './actions'
import {
  defaultItemsPerPage,
  MOOD_METER_GROUP_POSTGREST_ORDERS,
} from './constants'
import { GroupPlotsFiltersState } from './reducer'

const generateRange = (page: number) => {
  const from = page * defaultItemsPerPage
  return { from, to: from + defaultItemsPerPage - 1 }
}

export function* loadGroupPlotsSaga() {
  const { page, nameFilter, orderFilter } = (yield select(
    (state: AppState) => state.moodMeter.groupPlotsFilters,
  )) as GroupPlotsFiltersState

  // @ts-expect-error build-service requestSuccess typing does not reflect isArray services
  yield put(getMoodMeterGroupsService.actions.requestSuccess([] as MoodMeterGroup[], {}))

  yield put(
    getMoodMeterGroupsService.actions.request({
      range: generateRange(page),
      urlParams: {
        name: nameFilter || undefined,
        order: MOOD_METER_GROUP_POSTGREST_ORDERS[orderFilter],
      },
    } as Parameters<typeof getMoodMeterGroupsService.actions.request>[0]),
  )
}

function* clampGroupPlotsPageIfNeeded() {
  const { page } = (yield select(
    (state: AppState) => state.moodMeter.groupPlotsFilters,
  )) as GroupPlotsFiltersState
  const range = (yield select((state: AppState) => state.moodMeter.groups.range)) as
    | AppState['moodMeter']['groups']['range']
    | undefined

  if (_.isEmpty(range)) {
    return
  }

  const totalPages = Math.max(1, Math.ceil((range?.all ?? 0) / defaultItemsPerPage))
  const maxPage = totalPages - 1

  if (page > maxPage) {
    yield put(changeGroupPlotsPage(maxPage))
  }
}

export function* reloadGroupPlotsAfterDeleteSaga() {
  const { page, nameFilter } = (yield select(
    (state: AppState) => state.moodMeter.groupPlotsFilters,
  )) as GroupPlotsFiltersState

  // @ts-expect-error saga typing
  yield call(getMyGroupsService.requestSaga, { payload: {} })

  const myGroups = (yield select((state: AppState) => state.moodMeter.myGroups.items)) as
    | MoodMeterGroup[]
    | undefined
  const availableNames = _.uniq(_.map(myGroups, 'name'))

  if (nameFilter && !availableNames.includes(nameFilter)) {
    yield put(changeGroupPlotsNameFilter(''))
    return
  }

  const range = (yield select((state: AppState) => state.moodMeter.groups.range)) as
    | AppState['moodMeter']['groups']['range']
    | undefined
  const totalAfterDelete = Math.max(0, (range?.all ?? 1) - 1)
  const maxPage = Math.max(0, Math.ceil(totalAfterDelete / defaultItemsPerPage) - 1)
  const targetPage = Math.min(page, maxPage)

  if (targetPage !== page) {
    yield put(changeGroupPlotsPage(targetPage))
    return
  }

  yield call(loadGroupPlotsSaga)
}

export default function* groupPlotsFilterSaga() {
  yield all([
    takeLatest(
      [
        LOAD_GROUP_PLOTS_PAGE,
        CHANGE_GROUP_PLOTS_PAGE,
        CHANGE_GROUP_PLOTS_NAME_FILTER,
        CHANGE_GROUP_PLOTS_ORDER_FILTER,
      ],
      loadGroupPlotsSaga,
    ),
    takeLatest(RELOAD_GROUP_PLOTS_AFTER_DELETE, reloadGroupPlotsAfterDeleteSaga),
    takeLatest(getMoodMeterGroupsService.constants.REQUEST_SUCCESS, clampGroupPlotsPageIfNeeded),
  ])
}
